import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { subscriptionsApi } from '../../api/subscriptions'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import Button from '../../components/common/Button'
import './Subscriptions.css'

const Subscriptions = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['packages'],
    queryFn: () => subscriptionsApi.getPackages(),
    retry: (failureCount, error) => {
      // Don't retry on 401 errors (unauthorized)
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 2
    },
  })

  if (isLoading && !data) {
    return (
      <>
        <Header />
        <Loading fullScreen text="جاري التحميل..." />
        <Footer />
      </>
    )
  }

  const packages = data?.data?.packages || []

  // Handle 401 errors gracefully - show login required instead of error
  if (error?.response?.status === 401) {
    return (
      <div className="subscriptions-page bg-gray-900 min-h-screen">
        <Header />
        <main className="subscriptions-main bg-gray-900">
          <div className="subscriptions-background">
            <div className="background-circle background-circle-1"></div>
            <div className="background-circle background-circle-2"></div>
          </div>
          <div className="container mx-auto px-4 py-8">
            <div className="subscriptions-header">
              <h1 className="page-title text-white">باقات الاشتراك</h1>
            </div>
            {/* Login Required Banner */}
            <div className="login-required-banner">
              <div className="login-required-content">
                <div className="login-required-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div className="login-required-text">
                  <p className="login-required-title">تسجيل الدخول مطلوب</p>
                  <p className="login-required-description">يجب تسجيل الدخول للوصول إلى باقات الاشتراك</p>
                </div>
                <button 
                  className="login-required-button"
                  onClick={() => navigate('/login')}
                >
                  تسجيل الدخول
                </button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  // Show error message if API fails but still show page
  if (error && !data) {
    return (
      <>
        <Header />
        <main className="subscriptions-main bg-gray-900">
          <div className="subscriptions-background">
            <div className="background-circle background-circle-1"></div>
            <div className="background-circle background-circle-2"></div>
          </div>
          <div className="container mx-auto px-4 py-8">
            <div className="subscriptions-header">
              <h1 className="page-title text-white">باقات الاشتراك</h1>
            </div>
            <div className="error-message">
              <div className="error-icon">⚠️</div>
              <h3>حدث خطأ أثناء تحميل الباقات</h3>
              <p>يرجى المحاولة مرة أخرى</p>
              <Button variant="primary" onClick={() => refetch()} style={{ marginTop: '20px' }}>
                إعادة المحاولة
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </>
    )
  }

  return (
    <div className="subscriptions-page bg-gray-900 min-h-screen">
      <Header />
      <main className="subscriptions-main bg-gray-900">
        <div className="subscriptions-background">
          <div className="background-circle background-circle-1"></div>
          <div className="background-circle background-circle-2"></div>
        </div>
        <div className="container mx-auto px-4 py-8">
          <div className="subscriptions-header">
            <div className="header-content">
              <h1 className="page-title text-white">باقات الاشتراك</h1>
              <p className="page-subtitle">اختر الباقة المناسبة لك وابدأ رحلتك التعليمية</p>
            </div>
          </div>
          
          {/* Login Required Banner */}
          {!isAuthenticated && (
            <div className="login-required-banner">
              <div className="login-required-content">
                <div className="login-required-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div className="login-required-text">
                  <p className="login-required-title">تسجيل الدخول مطلوب</p>
                  <p className="login-required-description">يجب تسجيل الدخول للوصول إلى باقات الاشتراك</p>
                </div>
                <button 
                  className="login-required-button"
                  onClick={() => navigate('/login')}
                >
                  تسجيل الدخول
                </button>
              </div>
            </div>
          )}
          
          {packages.length > 0 ? (
            <div className="packages-list">
              {packages.map((pkg, index) => (
                <div key={pkg.id} className="package-card" style={{ animationDelay: `${index * 100}ms` }}>
                  <div className="package-card-inner">
                    <div className="package-card-header">
                      <div className="package-header-content">
                        <div className="package-name-wrapper">
                          <h3 className="package-name">{pkg.name}</h3>
                          <div className="package-badge">باقة مميزة</div>
                        </div>
                        <div className="package-duration">
                          <svg className="duration-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                          </svg>
                          <span>{pkg.duration_days || 30} يوم</span>
                        </div>
                      </div>
                      <div className="package-price-badge">
                        <div className="price-wrapper">
                          <div className="price-amount">{pkg.price || 0}</div>
                          <div className="price-currency">ج.م</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="package-card-content">
                      {pkg.description && (
                        <div className="package-description-wrapper">
                          <p className="package-description">{pkg.description}</p>
                        </div>
                      )}
                      
                      {/* Subjects and Courses Section */}
                      {(pkg.subjects?.length > 0 || pkg.courses?.length > 0) && (
                        <div className="package-includes">
                          {pkg.subjects?.length > 0 && (
                            <div className="includes-section">
                              <div className="includes-header">
                                <div className="includes-icon-wrapper">
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                                  </svg>
                                </div>
                                <span className="includes-title">المواضيع المشمولة</span>
                              </div>
                              <div className="includes-tags">
                                {pkg.subjects.map((subject, idx) => (
                                  <span key={idx} className="include-tag">
                                    {subject.name || subject}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {pkg.courses?.length > 0 && (
                            <div className="includes-section">
                              <div className="includes-header">
                                <div className="includes-icon-wrapper">
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                  </svg>
                                </div>
                                <span className="includes-title">الدورات المشمولة</span>
                              </div>
                              <div className="includes-list">
                                {pkg.courses.slice(0, 3).map((course, idx) => (
                                  <div key={idx} className="include-item">
                                    <div className="include-bullet"></div>
                                    <span className="include-text">{course.title || course}</span>
                                  </div>
                                ))}
                                {pkg.courses.length > 3 && (
                                  <div className="include-more">
                                    + {pkg.courses.length - 3} دورات إضافية
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Features */}
                      {pkg.features && pkg.features.length > 0 && (
                        <div className="package-features-section">
                          <h4 className="features-title">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                              <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                            المميزات
                          </h4>
                          <ul className="package-features">
                            {pkg.features.map((feature, index) => (
                              <li key={index} className="feature-item">
                                <div className="feature-check">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                  </svg>
                                </div>
                                <span className="feature-text">
                                  {typeof feature === 'object' ? feature.feature || feature.name : feature}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Subscribe Button */}
                      <Button
                        variant="primary"
                        fullWidth
                        size="lg"
                        onClick={() => {
                          // If package has subjects, navigate to subject selection first
                          if (pkg.subjects && pkg.subjects.length > 0) {
                            navigate(`/packages/${pkg.id}/subjects`)
                          } else {
                            navigate(`/payments?package_id=${pkg.id}`)
                          }
                        }}
                        className="subscribe-button"
                      >
                        <span>اشترك الآن</span>
                        <svg className="button-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <h3>لا توجد باقات متاحة حالياً</h3>
              <p>
                {isAuthenticated
                  ? 'لا توجد باقات تطابق تفضيلاتك الحالية. جرب تحديث تفضيلاتك لرؤية المزيد من الخيارات.'
                  : 'تحقق مرة أخرى لاحقاً للاطلاع على باقات الاشتراك'}
              </p>
              {isAuthenticated && (
                <Button
                  variant="primary"
                  onClick={() => navigate('/profile')}
                  style={{ marginTop: '24px' }}
                >
                  ⚙️ حدد تفضيلاتك
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Subscriptions

